// shell/Topbar/MobileTopBar.tsx — slim (~56px) mobile-only top bar.
//
// Slots (left → right):
//   • LogoTriad brand mark linking to "/"
//   • Search affordance: icon link to "/explore"
//   • NotificationBell (existing component, reused)
//   • "Panels" button (sliders icon) → fires onOpenPanels() to open the right-rail drawer
//   • Avatar / menu button: link to "/more"

import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { LogoTriad } from '@/icons/brand/LogoTriad';
import { Icon } from '@/icons';
import { IconButton } from '@/ui';
import { useSession } from '@/hooks';
import { NotificationBell } from './NotificationBell';

export interface MobileTopBarProps {
  onOpenPanels: () => void;
  /** When false, the Panels button is not rendered (e.g. pages with no right-rail content). */
  hasPanels?: boolean;
}

// ── Styled pieces ─────────────────────────────────────────────────────────────

const Bar = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 4px;
  height: 56px;
  padding: 0 12px;
  background: ${({ theme }) => theme.color.surfaceChrome};
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
`;

const BrandLink = styled(Link)`
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  flex: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.brandFrom};
    outline-offset: 2px;
  }
`;

const Spacer = styled.div`
  flex: 1;
`;

const SearchLink = styled(Link)`
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.color.textMuted};
  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.brandFrom};
    outline-offset: 2px;
  }
`;

const MoreLink = styled(Link)`
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.color.textMuted};
  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.brandFrom};
    outline-offset: 2px;
  }
`;

// 1.24.70: the guest's one door on mobile — a compact Sign in pill where the
// account chrome (bell, More) would otherwise sit. Carries ?next= like every
// other guest entry point.
const GuestSignIn = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.xs};
  text-decoration: none;
  border: 1px solid transparent;
  background: ${({ theme }) => theme.color.accentCyan};
  color: ${({ theme }) => theme.color.onAccent};
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

export function MobileTopBar({ onOpenPanels, hasPanels = true }: MobileTopBarProps) {
  const { isAuthenticated } = useSession();
  const location = useLocation();
  const next = encodeURIComponent(location.pathname + location.search);

  return (
    <Bar data-testid="mobile-top-bar">
      <BrandLink to="/" aria-label="TruXSocial home">
        <LogoTriad size={28} />
      </BrandLink>

      <Spacer />

      <SearchLink to="/explore" aria-label="Search">
        <Icon name="search" size={20} />
      </SearchLink>

      {/* 1.24.70: account chrome is for accounts. A guest gets Sign in instead
          of a bell and a More menu that both presume membership. */}
      {isAuthenticated && <NotificationBell />}

      {hasPanels && (
        <IconButton
          icon="sliders-horizontal"
          label="Open panels"
          onClick={onOpenPanels}
          data-testid="panels-button"
        />
      )}

      {isAuthenticated ? (
        <MoreLink to="/more" aria-label="More">
          <Icon name="more-horizontal" size={20} />
        </MoreLink>
      ) : (
        <GuestSignIn to={`/signin?next=${next}`}>Sign in</GuestSignIn>
      )}
    </Bar>
  );
}
